import { fetchBaseQuery, createApi } from "@reduxjs/toolkit/query/react";
import type {
  ConversationInterface,
  ConversationHistoryListItemInterface,
  ChatHistory,
  RagResponseObject,
} from "@/components/types";
import { getAuth } from "firebase/auth";
import { aiAssistantProxy } from "@/lib/firebaseFunctions";

// API Request/Response Types
export interface StartNewChatRequest {
  message: string;
  chatHistory: ChatHistory[] | [];
}

export interface GetConversationByIdRequest {
  conversationId: string;
}

export interface GetConversationByIdResponse {
  _id: string;
  version: number;
  user_name: string;
  conversation_name: string;
  chat_history: ChatHistory[];
  created_at: string;
  updated_at: string;
}

export interface CreateConversationRequest {
  chatHistory: ChatHistory[];
}

export interface CreateConversationResponse {
  conversation_name: string;
  conversation_id: string;
}

export interface UpdateConversationRequest {
  conversationId: string;
  newMessages: [ChatHistory, ChatHistory];
}

export interface UpdateConversationResponse {
  success: boolean;
}

const customBaseQuery = async (args: any, api: any, extraOptions: any) => {
  const auth = getAuth();
  const user = auth.currentUser;

  let jwt = "";
  if (user) {
    try {
      jwt = await user.getIdToken();
    } catch (error) {
      console.error("Failed to get JWT token:", error);
    }
  }

  const baseQuery = fetchBaseQuery({
    baseUrl: import.meta.env.VITE_RAG_API_BASE_URL,
    timeout: 7 * 60 * 1000, // 7 minutes
    prepareHeaders: async (headers) => {
      if (jwt) {
        headers.set("Authorization", `Bearer ${jwt}`);
      }
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

const CREATE_CONVERSATION = "/conversations/create_v1";
const GET_CONVERSATION_BY_ID = "/conversations/get_v1";
const GET_ALL_CONVERSATIONS = "/conversations/list_v1";
const UPDATE_CONVERSATION = "/conversations/update_v1";

export const conversationsApi = createApi({
  keepUnusedDataFor: 60,
  reducerPath: "conversationsApi",
  baseQuery: customBaseQuery,
  tagTypes: ["Conversation", "ConversationList"],
  endpoints: (builder) => ({
    sendQueryToChat: builder.mutation<RagResponseObject, StartNewChatRequest>({
      queryFn: async ({ message, chatHistory = [] }) => {
        const startTime = Date.now();

        try {
          const result = await aiAssistantProxy({
            query: message,
            chat_history: chatHistory,
          });

          const data = result.data as ConversationInterface;
          const ragResponseObject = JSON.parse(data.ragResponse) as RagResponseObject;

          return { data: ragResponseObject };
        } catch (error: any) {
          const elapsed = Date.now() - startTime;

          if (error?.code === "functions/deadline-exceeded") {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: "השאילתה לוקחת זמן רב מהרגיל. אנא נסה שוב או פשט את השאלה.",
                data: {
                  code: "DEADLINE_EXCEEDED",
                  message: "Request timed out",
                  elapsedSeconds: (elapsed / 1000).toFixed(2),
                },
              },
            };
          }

          if (error?.code === "functions/resource-exhausted") {
            const details = (error.details ?? {}) as {
              code?: string;
              appId?: string;
              required?: number;
              available?: number;
              quotaWindowEndsAt?: string | null;
            };
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: error.message || "אין מספיק קרדיטים. https://legalix.ai — Legalix +972 55-213-6446  email grembek@legalix.ai",
                data: {
                  code: details.code || "INSUFFICIENT_CREDITS",
                  appId: details.appId,
                  required: details.required,
                  available: details.available,
                  quotaWindowEndsAt: details.quotaWindowEndsAt ?? null,
                },
              },
            };
          }

          if (error?.code === "functions/failed-precondition") {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: error.message || "הגישה למנוי חסומה",
                data: { code: "SUBSCRIPTION_EXPIRED" },
              },
            };
          }

          if (error?.code === "functions/unauthenticated") {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: "נדרשת הזדהות מחדש",
                data: { code: "UNAUTHENTICATED" },
              },
            };
          }

          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error?.message || "שגיאה בקבלת תשובה מהבינה המלאכותית",
              data: {
                code: error?.code || "UNKNOWN",
                elapsedSeconds: (elapsed / 1000).toFixed(2),
              },
            },
          };
        }
      },
      invalidatesTags: ["ConversationList"],
    }),
    createConversation: builder.mutation<CreateConversationResponse, CreateConversationRequest>({
      query: ({ chatHistory }) => ({
        url: CREATE_CONVERSATION,
        method: "POST",
        body: { chat_history: chatHistory },
      }),
      invalidatesTags: ["ConversationList"],
    }),
    getConversationById: builder.query<GetConversationByIdResponse, GetConversationByIdRequest>({
      query: ({ conversationId }) => ({
        url: GET_CONVERSATION_BY_ID,
        method: "POST",
        body: { conversation_id: conversationId },
      }),
      providesTags: (_result, _error, { conversationId }) => [{ type: "Conversation", id: conversationId }],
    }),
    getAllConversations: builder.query<ConversationHistoryListItemInterface[], void>({
      query: () => ({
        url: GET_ALL_CONVERSATIONS,
        method: "POST",
        body: {},
      }),
      providesTags: ["ConversationList"],
    }),
    updateConversationById: builder.mutation<UpdateConversationResponse, UpdateConversationRequest>({
      query: ({ conversationId, newMessages }) => ({
        url: UPDATE_CONVERSATION,
        method: "POST",
        body: {
          conversation_id: conversationId,
          new_messages: newMessages,
        },
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversation", id: conversationId },
        "ConversationList",
      ],
    }),
  }),
});

export const {
  useSendQueryToChatMutation,
  useGetAllConversationsQuery,
  useGetConversationByIdQuery,
  useUpdateConversationByIdMutation,
  useCreateConversationMutation,
} = conversationsApi;
